if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.updateATCButton = this.updateATCButton.bind(this);

      this.form = this.querySelector('form');
      this.isPreOrder = this.dataset.isPreOrder === 'true';
      this.isBundle = this.dataset.isBundle === 'true';
      this.isPreorderBundle = this.dataset.isPreorderBundle === 'true';
      this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
      this.variantId = this.dataset.variantId;
      this.section = this.dataset.section;

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');

      if (this.isPreOrder || this.isPreorderBundle) {
        // mutationObserver for buy now button
        this.observerConfig = { attributes: true, childList: true, subtree: true, characterData: true };
        this.observer = new MutationObserver(this.updateATCButton);
        // Start observing the target node for configured mutations
        this.observer.observe(this.form, this.observerConfig);

        this.updateATCButton();
      }
    }

    updateATCButton() {
      this.atcButton = this.querySelector('.product-form__submit > span');
      const canAddToCart = this.atcButton.innerText.toLowerCase() === window.variantStrings.addToCart.toLowerCase();

      if (!canAddToCart) return;
      this.observer.disconnect();
      this.atcButton.innerText = 'Pre-Order Now';
      this.observer.observe(this.form, this.observerConfig);
    }

    async fetchCart() {
      const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? `${routes.cart_url}.js` : '/cart.js';
      const response = await fetch(cartUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      return response.json();
    }

    getSelectedQuantity() {
      const qtyInput = document.getElementById(`Quantity-${this.section}`);
      const value = qtyInput ? parseInt(qtyInput.value || 1, 10) : 1;
      return Number.isNaN(value) ? 1 : value;
    }

    findLinesByVariantId(cartItems, variantId) {
      const id = parseInt(variantId, 10);
      if (!id || !Array.isArray(cartItems)) return [];
      return cartItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item && item.id === id)
        .map(({ index }) => index);
    }

    buildBundleSignatureFromProperties(bundleProps) {
      if (!bundleProps || typeof bundleProps !== 'object') return null;

      const selectedKeys = Object.keys(bundleProps).filter((k) =>
        k.includes('_bundle_selectedVariantOnProduct_')
      );
      if (!selectedKeys.length) return null;

      const parts = [];

      for (const key of selectedKeys) {
        const productId = key.replace('_bundle_selectedVariantOnProduct_', '');
        const selectedVariantId = String(bundleProps[key]);

        // 1 by default if can't read JSON
        let qtyInBundle = 1;
        const dataKey = `_line_item_data_for_product_${productId}`;
        const raw = bundleProps[dataKey];

        if (raw) {
          try {
            const parsed = (typeof raw === 'string') ? JSON.parse(raw) : raw;
            const q = parseInt(parsed['quantity in bundle'], 10);
            if (!Number.isNaN(q) && q > 0) qtyInBundle = q;
          } catch (_) {}
        }

        parts.push(`${productId}:${selectedVariantId}:${qtyInBundle}`);
      }

      parts.sort((a, b) => {
        const pa = parseInt(a.split(':')[0], 10);
        const pb = parseInt(b.split(':')[0], 10);
        if (Number.isNaN(pa) || Number.isNaN(pb)) return a.localeCompare(b);
        return pa - pb;
      });

      return parts.join('|');
    }

    findLinesByVariantIdAndBundleSignature(cartItems, variantId, bundleSignature) {
      const id = parseInt(variantId, 10);
      if (!id || !bundleSignature || !Array.isArray(cartItems)) return [];

      return cartItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => {
          if (!item || item.id !== id) return false;
          const sig = this.buildBundleSignatureFromProperties(item.properties);
          return sig === bundleSignature;
        })
        .map(({ index }) => index);
    }

    async postCartChange(line, quantity, sections, sections_url) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({ line, quantity, sections, sections_url });

      const response = await fetch(`${routes.cart_change_url}`, config);
      return response.json();
    }

    async mergeAddToCartByVariantId(variantId, addQty, sections, sections_url, bundleSignature = null) {
      // Non-bundle: merge by variantId.
      // Bundle: only merge if we have a bundle signature (same components). 
      const cart = await this.fetchCart();
      const matches = bundleSignature
        ? this.findLinesByVariantIdAndBundleSignature(cart.items, variantId, bundleSignature)
        : this.findLinesByVariantId(cart.items, variantId);
      if (!matches.length) return null;

      const totalExistingQty = matches.reduce((sum, idx) => {
        const q = cart.items[idx] ? parseInt(cart.items[idx].quantity, 10) : 0;
        return sum + (Number.isNaN(q) ? 0 : q);
      }, 0);

      const desiredQty = totalExistingQty + addQty;

      // Update the first matching line with the combined qty
      const primaryLine = matches[0] + 1;
      let finalResponse = await this.postCartChange(primaryLine, desiredQty, sections, sections_url);

      // If there are already duplicates, remove the extra lines one-by-one (avoid /cart/update.js bulk issues)
      for (let i = 1; i < matches.length; i++) {
        const lineToRemove = matches[i] + 1;
        finalResponse = await this.postCartChange(lineToRemove, 0, sections, sections_url);
      }

      return finalResponse;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      const submitButton = this.querySelector('[type="submit"]');
      if (submitButton.classList.contains('loading')) return;

      this.handleErrorMessage();
      if (evt.target.closest(".upsell")) { this.cartNotification.setActiveElement(document.activeElement) };

      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const isValid = this.validateInventory();

      if (!isValid) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        return;
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = JSON.parse(serializeForm(this.form));
      const sections = this.cartNotification.getSectionsToRender().map((section) => section.id);
      const sections_url = window.location.pathname;

      config.body = JSON.stringify({
        ...formData,
        sections,
        sections_url
      });

      const isBundleAdd = (this.isBundle || this.isPreorderBundle);
      const bundleSignature = isBundleAdd
        ? this.buildBundleSignatureFromProperties(formData.properties)
        : null;
      const isMergeEligible = (!isBundleAdd) || !!bundleSignature;
      const addQty = parseInt(formData.quantity || this.getSelectedQuantity(), 10) || 1;

      const requestPromise = (isMergeEligible && formData.id)
        ? this.mergeAddToCartByVariantId(formData.id, addQty, sections, sections_url, bundleSignature)
          .then((mergedResponse) => mergedResponse || fetch(`${routes.cart_add_url}`, config).then((r) => r.json()))
          .catch(() => fetch(`${routes.cart_add_url}`, config).then((r) => r.json()))
        : fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

      requestPromise
        .then((response) => {
          let errorMessage = response.description || response.message;
          if (response.status === 422) {
            errorMessage = `${errorMessage}\nIf the product is in your cart, please remove it and try again.`;
            this.handleErrorMessage(errorMessage);
            return;
          }
          let open = evt.target.closest(".upsell") ? false : true

          // Ensure CartNotification can find the correct product markup in the returned sections
          if (response && !response.id) {
            const formData = JSON.parse(serializeForm(this.form));
            response.id = formData.id; // variant id
          }
          this.cartNotification.renderContents(response, open);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          this.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          this.dispatchEvent(new CustomEvent('cart:updateCloseOut', { bubbles: true }));
          // If upsell section present, refresh cart from cart add
          if (evt.target.closest(".upsell")) { document.querySelector('cart-items').getNewCart() }
        });
    }

    validateInventory() {
      const selectedQuantity = parseInt(document.getElementById(`Quantity-${this.section}`).value || 1, 10);
      const variantSelect = document.querySelector(`variant-selects[data-section="${this.section}"], variant-radios[data-section="${this.section}"]`);
      const bundleInfo = document.querySelector(`#BundleInfo-${this.section}`);

      if (this.isBundle) {
        let isValid = true;
        let isAvailable = bundleInfo && bundleInfo.available;
        if (!isAvailable) return false;

        let formData = JSON.parse(serializeForm(this.form));
        let atcVariants = this.expandBundleFormProperties(formData.properties);

        for (const [variantId, bundle] of Object.entries(atcVariants)) {
          let variant = window[this.section].variantsById[variantId];
          variant.safetyStock = variant.safety_stock;
          const availableInventory = window.inventoryValidator.getSellableInventoryForVariant(variant);

          if (availableInventory < (selectedQuantity * bundle.quantity)) {
            const message = window.cartStrings.bundleQuantityError;
            this.handleErrorMessage(message);
            isValid = false;
            break;
          }
        }

        return isValid;

      } else {
        // products without variants
        if (this.hasOnlyDefaultVariant && this.variantId) {
          this.currentVariant = window[this.section].variantsById[this.variantId];
        } else if (!this.hasOnlyDefaultVariant) {
          if (variantSelect && variantSelect.currentVariant) {
            this.variantId = variantSelect.currentVariant.id;
            this.currentVariant = window[this.section].variantsById[this.variantId]
          }
        }

        this.currentVariant.safetyStock = window[this.section].safetyStock;
        const availableInventory = window.inventoryValidator.getSellableInventoryForVariant(this.currentVariant);

        if (availableInventory < selectedQuantity) {
          const message = window.cartStrings.quantityError.replace('[quantity]', availableInventory);
          this.handleErrorMessage(message);
        }

        return availableInventory >= selectedQuantity;
      }
    }

    expandBundleFormProperties(bundleData) {
      const expandedData = {};

      // get quantity info for each line item in the bundle
      let bundleLineItemData = {};
      let bundleLineItems = Object.entries(bundleData).filter(([key]) => key.includes('_line_item_data_for_product_'));

      bundleLineItems.forEach(lineItem => {
        let productId = lineItem[0].replace('_line_item_data_for_product_', '');
        let data = JSON.parse(lineItem[1]);
        let quantity = data['quantity in bundle'];
        let safetyStock = data['safety stock'];
        bundleLineItemData[productId] = bundleLineItemData[productId] || { "quantity": quantity, "safetyStock": safetyStock };
      })

      // add each variant in the bundle to expandedData
      let bundleItemVariants = Object.entries(bundleData).filter(([key]) => key.includes('_bundle_selectedVariantOnProduct_'));

      bundleItemVariants.forEach(variant => {
        let productId = variant[0].replace('_bundle_selectedVariantOnProduct_', '');
        let variantId = variant[1];

        expandedData[variantId] = expandedData[variantId] || { quantity: 0, productId: productId };
        expandedData[variantId].quantity += bundleLineItemData[productId].quantity;
        expandedData[variantId].safetyStock = bundleLineItemData[productId].safetyStock;
      });

      return expandedData;
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
